import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Head from 'next/head';
import SharedLib from 'wizarding-world-web-shared';
import { resetProfilePagePosition } from '@utils/profile';
import { trackEvent } from '@utils/analytics';
import { useGlobalState as useNavigationGlobalState } from '@/src/globalStates/NavigationState';
import Image from '../Image';
import AnchorLink from '../AnchorLink';
import { useNavContext } from '../../contexts/nav-context';
import Header from '../Header';
import HouseBackgroundOrigin from '../../pages/Profile/components/HouseBackgroundOrigin';
import Footer from '../Footer';
import { useWindowWidth } from '../../hooks/windowHooks';
import s from './ProfileLayout.module.scss';
import m from '../../pages/Profile/Profile.module.scss';
import UserPanel from '../../pages/Profile/components/UserPanel';
import ContentPanel from '../../pages/Profile/components/ContentPanel';
import { ReactComponent as SettingsIcon } from '../../svgs/settings-icon.svg';
import { useUserContext } from '../../contexts/user-context';
import { useGlobalState } from '../../globalStates/ProfileState';
import {
  setFirstLoadHouse,
  useGlobalState as houseAnimationStartedGlobalState,
} from '../../globalStates/ProfileThemeState';
import { PROFILE, PROFILE_SETTINGS } from '../../constants/routes';
import { breakpoints } from '../../hooks/useBreakpoints';
import { useGlobalState as useRouteLoadingState } from '../../globalStates/RouteLoadingState';
import Loaders from '../Loaders';
import loaderTypes from '../Loaders/loaderTypes';
import { PROFILE_FIRSTLOAD_HOUSE } from '../../constants';
import close from './img/close-icon.svg';

const propTypes = {
  children: PropTypes.node,
  path: PropTypes.string,
  location: PropTypes.string,
  activeModal: PropTypes.bool,
  hideMobile: PropTypes.bool,
};

const pagesWithoutContentPanel = ['sorting-intro', 'wand-intro'];
const ProfileLayout = ({
  children = <></>,
  path = null,
  location = 'Wizarding World - User Profile',
  activeModal = false,
  hideMobile = false,
}) => {
  const [blur] = useNavigationGlobalState('blur');
  const [navRibbonActive] = useNavigationGlobalState('navRibbonActive');
  const { footerData, navData } = useNavContext();
  const { profile } = useUserContext();
  const [houseAnimationStarted] = houseAnimationStartedGlobalState('houseAnimationStarted');
  const [profileData] = useGlobalState('profileData');
  const [isLoadingPage] = useRouteLoadingState('isLoadingPage');
  const width = useWindowWidth();
  const [isMobile, setIsMobile] = useState(null);
  const [showHouseColor, setShowHouseColor] = useState(false);
  const isProfileRoot = path === '';

  const imageSet = [
    {
      src: close,
    },
  ];

  const { hogwartsHouse: house } = profile;
  const houseName = house ? house.toLowerCase() : 'noHouse';

  // check for first load cookies here
  const { getCookie, eraseCookie } = SharedLib.utils.cookie;
  const firstLoadHouse = getCookie('firstLoadHouse') === 'true';

  let pageOutcomeClass = 'house';
  if (path && path.length) {
    if (path.includes('wand')) {
      pageOutcomeClass = 'wand';
    } else if (path.includes('patronus')) {
      pageOutcomeClass = 'patronus';
    }
  }

  useEffect(() => {
    if (isMobile) {
      eraseCookie(PROFILE_FIRSTLOAD_HOUSE);
      setFirstLoadHouse(false);
    } else {
      setFirstLoadHouse(firstLoadHouse);
    }
  }, [eraseCookie, firstLoadHouse, isMobile]);

  useEffect(() => {
    if (houseAnimationStarted) {
      setShowHouseColor(true);
    }
  }, [houseAnimationStarted]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(width < breakpoints.xm);
    };
    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, [activeModal, isMobile, width]);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>
      <div
        className={cx(
          m.house,
          !firstLoadHouse || showHouseColor || isMobile ? m[houseName] : m.noHouse,
          s[location.replace(/[^a-z0-9+]+/gi, '')],
          hideMobile ? s.fullScreenMobile : '',
        )}
      >
        {house && firstLoadHouse && <HouseBackgroundOrigin house={houseName} />}
        {navData && <Header navData={navData} />}
        <section
          data-testid="profileLayout"
          className={cx(
            s.profileWrapper,
            navRibbonActive ? s.activeRibbon : '',
            blur ? s.blur : '',
          )}
        >
          <div className={s.twoColumnWrapper}>
            {!isLoadingPage && isProfileRoot && (
              <AnchorLink
                className={s.settingsLink}
                href={PROFILE_SETTINGS}
                onClick={
                  /* istanbul ignore next */ () => {
                    resetProfilePagePosition();
                    trackEvent('Settings Clicked', {
                      location,
                    });
                  }
                }
                data-testid="settings-gear-link"
              >
                <SettingsIcon />
              </AnchorLink>
            )}
            {!(activeModal && isMobile) && (
              <div className={cx(s.leftColumn, hideMobile ? s.hideMobile : '')}>
                {profileData && <UserPanel profileData={profileData} />}
              </div>
            )}
            <div
              className={cx([
                s.rightColumn,
                activeModal ? s.activeModal : '',
                path && `${pageOutcomeClass}-outcome`,
              ])}
            >
              {isLoadingPage && (
                <div className={m.profileLoader}>
                  <Loaders type={loaderTypes.timeturner} />
                </div>
              )}
              {!pagesWithoutContentPanel.includes(path) && profileData && !isLoadingPage && (
                <ContentPanel profileData={profileData} activeModal={activeModal} />
              )}
              {!isLoadingPage && (
                <div className={[s.childContainer, s.activeModal].join(' ')}>
                  {activeModal && (
                    <AnchorLink
                      href={PROFILE}
                      title="Close"
                      className={s.closeBtn}
                      onClick={resetProfilePagePosition}
                    >
                      <Image imageSet={imageSet} alt="close" />
                    </AnchorLink>
                  )}
                  {!isLoadingPage && children}
                </div>
              )}
            </div>
          </div>
        </section>
        {footerData && <Footer footerData={footerData} />}
      </div>
    </>
  );
};

ProfileLayout.propTypes = propTypes;
export default ProfileLayout;
